/**
 * Types for the client data export pipeline (the `exports` DynamoDB
 * table). An export pulls one agency's rows out of the shared production
 * Postgres database into per-table CSVs in S3, validates tenant isolation,
 * copies the agency's file attachments across, and provisions a locked-down
 * SFTP user on the shared Transfer Family server so the outgoing client can
 * collect their data.
 *
 * Mirrors the shape/philosophy of `Job` in job.ts: timestamps are ISO-8601
 * strings. Secrets (the
 * SFTP password) are never stored on the document — they are fetched on demand
 * from Secrets Manager via the getExportCredentials callable.
 */
/**
 * Allow-list of tables eligible for export (ported from the standalone
 * pipeline's config.yml, de-duplicated). A table is exported when it appears
 * here AND is tenant-scopable in the source database — either directly (it
 * carries an `agency_id` column) or indirectly (it carries a foreign key that a
 * `JOIN_SCOPE_RULES` entry maps to a parent that has `agency_id`). The scope for
 * each table is resolved at runtime by introspecting the live schema.
 */
export const EXPORT_TABLE_ALLOWLIST = [
    'candidates',
    'client_candidate_notes',
    'companies',
    'company_contacts',
    'company_custom_attribute_values',
    'company_identities',
    'company_locations',
    'company_managers',
    'company_notes',
    'company_relationships',
    'custom_attribute_options',
    'custom_attributes',
    'educations',
    'experiences',
    'fee_types',
    'files',
    'gdpr_emails',
    'interview_custom_attribute_values',
    'interview_notes',
    'interview_people',
    'interview_users',
    'interviews',
    'meetings',
    'people',
    'people_notes',
    'person_identities',
    'people_references',
    'people_salaries',
    'person_custom_attribute_values',
    'project_custom_attribute_values',
    'project_fee_splits',
    'project_company_contacts',
    'project_users',
    'projects',
    'project_stages',
    'todos',
    'users',
];
export const JOIN_SCOPE_RULES = [
    { fkColumn: 'project_id', parentTable: 'projects' },
    { fkColumn: 'interviews_id', parentTable: 'interviews' },
];
/** Lowercase a value and strip anything unsafe for an S3 key / SFTP username. */
export function slugifyCompany(value) {
    return value
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_-]/g, '')
        .replace(/_+/g, '_')
        .replace(/^[_-]+|[_-]+$/g, '');
}
/** Build the S3 key prefix an export writes under, e.g. "ryton/". */
export function buildExportPrefix(companyName) {
    return `${slugifyCompany(companyName)}/`;
}
//# sourceMappingURL=export.js.map